import axios from "axios";
import { DataGrid } from "devextreme-react";
import {
  Column,
  Editing,
  Form,
  LoadPanel,
  Popup,
  Scrolling,
  SearchPanel,
  Selection,
  StateStoring,
  Texts,
  Toolbar,
} from "devextreme-react/data-grid";
import {
  EmptyItem,
  Label,
  RequiredRule,
  SimpleItem,
} from "devextreme-react/form";
import { Item } from "devextreme-react/toolbar";
import CustomStore from "devextreme/data/custom_store";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getDateString } from "../common/date";
import JobDescriptionHtmlEditor from "./JobDescriptionHtmlEditor";

export default function CareerHistory() {
  const [dataSource, setDataSource] = useState(null);

  const userInfo = useSelector((state) => state.userInfo);

  const jobDescriptionHtmlEditorRef = useRef(null);
  const careerHistoryGridRef = useRef(null);

  useEffect(() => {
    setDataSource(
      new CustomStore({
        key: "SEQ",
        load: async () => {
          const result = await axios.get(
            `${process.env.REACT_APP_BECCO_API_URL}/api/cv/careerHistory/${userInfo.employeeNo}`
          );

          return result.data;
        },
        insert: async (values) => {
          await axios.post(
            `${process.env.REACT_APP_BECCO_API_URL}/api/cv/careerHistory/${userInfo.employeeNo}`,
            {
              ...values,
              jobDescr:
                jobDescriptionHtmlEditorRef.current.instance.option("value"),
            }
          );
        },
        update: async (key, values) => {
          await axios.put(
            `${process.env.REACT_APP_BECCO_API_URL}/api/cv/careerHistory/${userInfo.employeeNo}/${key}`,
            {
              ...values,
              jobDescr:
                jobDescriptionHtmlEditorRef.current.instance.option("value"),
            }
          );
        },
        remove: async (key) => {
          await axios.delete(
            `${process.env.REACT_APP_BECCO_API_URL}/api/cv/careerHistory/${userInfo.employeeNo}/${key}`
          );
        },
      })
    );
  }, []);

  const calculateDisplayValue = (data) => {
    return `${getDateString(new Date(data.startDate))}~${
      data.ps == 1 ? "Present" : getDateString(new Date(data.endDate))
    }`;
  };

  //   const calculateJobDisplayValue = (data) => {
  //     return `${data.coName}, ${data.jobTitle} <br/> ${data.jobDescr}`;
  //   };

  const cellRender = (data) => {
    return (
      <div>
        <span style={{ textDecoration: "underline" }}>
          {data.data.coName}, {data.data.jobTitle}
        </span>
        <div dangerouslySetInnerHTML={{ __html: data.data.jobDescr }} />
      </div>
    );
  };

  const onEditingStart = (e) => {
    e.component.selectRows(e.key, true);
  };

  const onHiding = (e) => {
    careerHistoryGridRef.current.instance.deselectAll();

    // jobDescriptionHtmlEditorRef.current.instance.reset();
  };

  const onSaving = React.useCallback(async (e) => {
    if (e.changes.length == 0) {
      const SEQ =
        careerHistoryGridRef.current.instance.option("editing.editRowKey");

      await axios.put(
        `${process.env.REACT_APP_BECCO_API_URL}/api/cv/careerHistory/${userInfo.employeeNo}/${SEQ}`,
        {
          jobDescr:
            jobDescriptionHtmlEditorRef.current.instance.option("value"),
        }
      );

      careerHistoryGridRef.current.instance.refresh();
    }
  });

  const setCellValue = (newData, value) => {
    newData.ps = value == "1" ? true : false;
  };

  const setEndDateCellValue = (newData, value) => {
    newData.endDate = value;
  };

  const onInitNewRow = (e) => {
    e.data.ps = false;
  };

  const customizeItem = (item) => {
    if (item) {
      let gridInstance = careerHistoryGridRef.current.instance;
      let editRowKey = gridInstance.option("editing.editRowKey");
      let index = gridInstance.getRowIndexByKey(editRowKey);
      index = index === -1 ? 0 : index;

      if (item.dataField == "endDate") {
        if (gridInstance.cellValue(index, "ps") == true) {
          item.disabled = true;
        }
      } else if (item.dataField == "ps") {
        if (gridInstance.cellValue(index, "endDate")) {
          item.disabled = true;
        }
      }
    }
  };

  return (
    <DataGrid
      id="careerHistoryGrid"
      ref={careerHistoryGridRef}
      dataSource={dataSource}
      rowAlternationEnabled={true}
      showBorders={true}
      showColumnLines={true}
      allowColumnResizing={true}
      columnAutoWidth={true}
      wordWrapEnabled={true}
      allowColumnReordering={true}
      style={{
        maxHeight: "calc(100vh - 180px)",
        marginLeft: "10px",
        marginRight: "10px",
        // marginTop: "56px",
      }}
      hoverStateEnabled={true}
      showColumnHeaders={false}
      onEditingStart={onEditingStart}
      onSaving={onSaving}
      onInitNewRow={onInitNewRow}
    >
      <LoadPanel enabled={true} />
      <SearchPanel visible={true} />
      <Scrolling mode={"virtual"} showScrollbar={"always"} />
      <Selection mode={"single"} />
      {/* <StateStoring
        enabled={true}
        type="localStorage"
        storageKey={"CurriculumVitae.CareerHistoryGrid"}
      /> */}
      <Editing
        mode={"popup"}
        allowAdding={true}
        allowDeleting={true}
        allowUpdating={true}
      >
        <Texts
          confirmDeleteTitle={"Delete the Career History"}
          confirmDeleteMessage={"Are you sure you want to delete this career?"}
        />
        <Popup
          title={"Career"}
          showTitle={true}
          height={600}
          onHiding={onHiding}
        />
        <Form
          labelMode={"outside"}
          showColonAfterLabel={false}
          labelLocation="top"
          minColWidth={300}
          colCount={2}
          colCountByScreen={{ sm: 2, md: 4 }}
          screenByWidth={(width) => {
            return width < 720 ? "sm" : "md";
          }}
          validationGroup="careerHistory"
          height={"100%"}
          customizeItem={customizeItem}
        >
          <SimpleItem
            dataField={"startDate"}
            editorType="dxDateBox"
            editorOptions={{
              type: "date",
              displayFormat: "yyyy-MM-dd",
              dateSerializationFormat: "yyyy-MM-dd",
              showClearButton: true,
              max: new Date(),
            }}
          >
            <Label text={"Start Date"} />
            <RequiredRule message={"* Start Date를 입력해주세요."} />
          </SimpleItem>
          <SimpleItem
            dataField={"endDate"}
            editorType="dxDateBox"
            editorOptions={{
              type: "date",
              displayFormat: "yyyy-MM-dd",
              dateSerializationFormat: "yyyy-MM-dd",
              showClearButton: true,
              max: new Date(),
              //   disabled: present == true ? true : false,
            }}
          >
            <Label text={"End date"} />
            {/* <CustomRule validationCallback={validationCallback} /> */}
          </SimpleItem>
          <SimpleItem
            dataField={"ps"}
            editorType="dxCheckBox"
            editorOptions={{
              text: "Present",
              // defaultValue: false,
              //   onValueChanged: onValueChanged,
            }}
          >
            <Label visible={false} />
          </SimpleItem>
          <EmptyItem />
          <SimpleItem
            dataField={"coName"}
            editorType="dxTextBox"
            editorOptions={{
              showClearButton: true,
            }}
            colSpan={2}
          >
            <Label text={"Company Name"} />
            <RequiredRule message={"* Company Name을 입력해주세요."} />
          </SimpleItem>
          <SimpleItem
            dataField={"jobTitle"}
            editorType="dxTextBox"
            editorOptions={{
              showClearButton: true,
            }}
            colSpan={2}
          >
            <Label text={"Job Title"} />
            <RequiredRule message={"* Job Title을 입력해주세요."} />
          </SimpleItem>
          <SimpleItem dataField={"jobDescr"} colSpan={4}>
            <Label text={"Job Description"} />
            <RequiredRule message={"* Job Description을 입력해주세요."} />
            <JobDescriptionHtmlEditor
              jobDescriptionHtmlEditorRef={jobDescriptionHtmlEditorRef}
              careerHistoryGridRef={careerHistoryGridRef}
            />
          </SimpleItem>
        </Form>
      </Editing>

      <Column
        dataField={"startDate"}
        caption="Start Date"
        dataType={"date"}
        editorOptions={{
          dateSerializationFormat: "yyyy-MM-dd",
        }}
        visible={false}
      />
      <Column
        dataField="endDate"
        caption="End Date"
        dataType="date"
        format="yyyy-MM-dd"
        editorOptions={{
          dateSerializationFormat: "yyyy-MM-dd",
        }}
        visible={false}
        setCellValue={setEndDateCellValue}
      />
      <Column
        dataField={"ps"}
        caption="Present"
        dataType={"string"}
        visible={false}
        setCellValue={setCellValue}
      />
      <Column
        dataField={"duration"}
        caption="Duration"
        dataType={"string"}
        // calculateCellValue={calculateCellValue}
        calculateDisplayValue={calculateDisplayValue}
        width={200}
      />
      <Column
        dataField={"coName"}
        caption="Company Name"
        dataType={"string"}
        visible={false}
      />
      <Column dataField={"jobTitle"} dataType="string" visible={false} />
      <Column dataField={"jobDescr"} dataType="string" visible={false} />
      <Column
        dataField={"job"}
        dataType="string"
        // calculateDisplayValue={calculateJobDisplayValue}
        cellRender={cellRender}
      />
      <Toolbar>
        <Item location={"before"}>
          <span
            style={{ fontWeight: 500, color: "#009688", marginLeft: "10px" }}
          >
            CAREER HISTORY
          </span>
        </Item>
        <Item location={"after"} name="addRowButton" />
        <Item location={"after"} name="searchPanel" />
      </Toolbar>
    </DataGrid>
  );
}
